const VALUES_PREFIX = "values.";

export const collectIntegerFieldsFromForms = (
  forms: unknown[][]
): Set<string> => {
  const integerFields = new Set<string>();

  forms.forEach((step) => {
    (step as Array<{ type?: string; field?: string }>).forEach((field) => {
      if (field?.type === "integer" && field.field) {
        integerFields.add(field.field);
      }
    });
  });

  return integerFields;
};

const coerceIntegerValue = (value: unknown): unknown => {
  if (value === "" || value == null) {
    return value;
  }

  return Number(value);
};

const resolveSubmitObjectTemplate = (
  template: unknown,
  values: Record<string, unknown>,
  integerFields: Set<string>
): unknown => {
  if (template === null || template === undefined) {
    return template;
  }

  if (typeof template === "string" && template.startsWith(VALUES_PREFIX)) {
    const fieldName = template.slice(VALUES_PREFIX.length);
    const resolvedValue = values[fieldName];

    if (integerFields.has(fieldName)) {
      return coerceIntegerValue(resolvedValue);
    }

    return resolvedValue;
  }

  if (Array.isArray(template)) {
    return template.map((item) =>
      resolveSubmitObjectTemplate(item, values, integerFields)
    );
  }

  if (typeof template === "object") {
    return Object.fromEntries(
      Object.entries(template as Record<string, unknown>).map(([key, value]) => [
        key,
        resolveSubmitObjectTemplate(value, values, integerFields),
      ])
    );
  }

  return template;
};

export const buildSubmittedCameraObject = (
  submitObject: Record<string, unknown>,
  values: Record<string, unknown>,
  forms?: unknown[][]
): Record<string, unknown> => {
  const integerFields = forms
    ? collectIntegerFieldsFromForms(forms)
    : new Set<string>();

  return resolveSubmitObjectTemplate(
    submitObject,
    values,
    integerFields
  ) as Record<string, unknown>;
};

export const getSubmitObjectFromAddCameraInfo = (): Record<
  string,
  unknown
> | undefined => {
  if (typeof localStorage === "undefined" || !localStorage.addCameraInfo) {
    return undefined;
  }

  try {
    const addCameraInfo = JSON.parse(localStorage.addCameraInfo);
    return addCameraInfo?.submit_object;
  } catch {
    return undefined;
  }
};

export const createSubmittedCameraObjectLegacy = (values: any) => {
  const customerId = Number(localStorage.siteId);

  const returnObject: any = {
    camera_name: values.camera_name,
    customer: customerId,
    integration: Number(localStorage.integrationType),
    http: Number(values.http),
    ip: values.ip,
    username: values.username,
    password: values.password,
    channel: values.channel,
    tcp: Number(values.tcp),
    rtsp: Number(values.rtsp),
    camera_type: values.camera_type,
    stream_parameters: values.stream_parameters,
    products: values.products,
    een_api: values.een_api,
    app_name: values.app_name,
    api_key: values.api_key,
    username_star4live: values.username_star4live,
    password_star4live: values.password_star4live,
    device_name: values.device_name,
    software_type_smartpss: values.software_type_smartpss,
    immix: {
      immix_id: values.immix_id,
      immix_server: values.immix_server,
      immix_email: values.immix_email,
      immix_port: values.immix_port,
      immix_input1: values.immix_input1,
    },
    dice: {
      dice_id: values.dice_id,
      dice_server: values.dice_server,
      dice_email: values.dice_email,
      dice_port: values.dice_port,
      dice_input1: values.dice_input1,
    },
    sentinel: {
      sentinel_server: values.sentinel_server,
      sentinel_site_id: values.sentinel_site_id,
      sentinel_device_address: values.sentinel_device_address,
      draw_ignore_zones: values.draw_ignore_zones,
    },
    bold: {
      bold_server: values.bold_server,
      bold_port: values.bold_port,
      bold_id: values.bold_id,
      bold_sdnis: values.bold_sdnis,
      bold_zone: values.bold_zone,
      bold_alarmtype: values.bold_alarmtype,
    },
    sureview: {
      sureview_server: values.sureview_server,
      sureview_subject: values.sureview_subject,
      sureview_cc: values.sureview_cc,
      sureview_email: values.sureview_email,
    },
    patriot: {
      patriot_server: values.patriot_server,
      patriot_username: values.patriot_username,
      patriot_password: values.patriot_password,
      patriot_zone_number: values.patriot_zone_number,
      patriot_send_video: values.patriot_send_video,
      patriot_client_no: values.patriot_client_no,
      patriot_type_number: values.patriot_type_number,
      draw_ignore_zones: values.draw_ignore_zones,
    },
    sysaid: {
      sysaid_server: values.sysaid_server,
      sysaid_client_local: values.sysaid_client_local,
      sysaid_username: values.sysaid_username,
      sysaid_password: values.sysaid_password,
    },
    evalink: {
      evalink_token: values.evalink_token,
      evalink_device_id: values.evalink_device_id,
      evalink_company_id: values.evalink_company_id,
      partition_id: values.partition_id,
      alarm_zone_id: values.alarm_zone_id,
    },
    lisa: {
      lisa_server: values.lisa_server,
      lisa_token: values.lisa_token,
      draw_ignore_zones: values.draw_ignore_zones,
    },
    camera_view: values.camera_view,
    dynamic_slicing: values.dynamic_slicing,
  };

  if (values.webhook) {
    returnObject.webhook = {
      stream: values.stream,
      webhook: values.webhook,
      custom_fields: values.custom_fields,
      event_types: values.event_types,
    };
  }

  if (values.server) {
    returnObject.softguard = {
      server: values.server,
      port: values.port,
      zone: values.zone,
      account: values.account,
      user: values.user,
      user_ext: values.user_ext,
    };
  }

  return returnObject;
};

export const createSubmittedCameraObject = (
  values: any,
  submitObject?: Record<string, unknown>,
  forms?: unknown[][]
) => {
  const resolvedSubmitObject =
    submitObject ?? getSubmitObjectFromAddCameraInfo();

  if (resolvedSubmitObject) {
    return buildSubmittedCameraObject(resolvedSubmitObject, values, forms);
  }

  return createSubmittedCameraObjectLegacy(values);
};
